import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet as Head } from "react-helmet-async";
import { ArrowRight, ChevronRight, ExternalLink, PencilRuler, Zap, Camera } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { MarketingHeader } from "@/components/MarketingHeader";
import { MarketingFooter } from "@/components/MarketingFooter";
import { updateMetaTags, SchemaScript, generateBreadcrumbSchema } from "@/lib/seoHelpers";
import {
  getEquipmentByPath,
  getRelatedEquipment,
  getAlternativeEquipment,
  getCamerasForMount,
  getSpecRows,
  getPlanningNote,
  categoryInfo,
  MOUNT_KIND_LABEL,
  type EquipmentPageData,
} from "@/lib/equipmentSlug";

const SITE = "https://cctvplanner.io";
/** Cameras listed under a mount; those with a page of their own come first. */
const MAX_CAMERAS = 90;

/** The figure that leads the title: ports and budget, VA and W, volts and amps, the mount's kind. */
function titleHook(e: EquipmentPageData, one: string): string {
  const s = e.specs as Record<string, number | string | undefined>;
  switch (e.category) {
    case "poe-switch":
      if (s.totalPorts === undefined) return one;
      return s.poeBudgetW !== undefined ? `${s.totalPorts}-port PoE switch, ${s.poeBudgetW} W` : `${s.totalPorts}-port PoE switch`;
    case "ups":
      if (s.capacityVA === undefined) return one;
      return `${s.capacityVA} VA${s.capacityW !== undefined ? ` / ${s.capacityW} W` : ""}${s.topology !== undefined ? ` ${s.topology}` : ""} UPS`;
    case "power-supply":
      return s.outputVoltageV !== undefined && s.outputA !== undefined ? `${s.outputVoltageV} V DC, ${s.outputA} A power supply` : one;
    case "mount":
      return e.mountKind ? MOUNT_KIND_LABEL[e.mountKind] : one;
    default:
      return one;
  }
}

function subtitle(e: EquipmentPageData): string {
  const s = e.specs as Record<string, number | string | undefined>;
  if (e.category === "poe-switch") {
    if (s.poePorts === undefined) return "";
    return s.poeStandard !== undefined ? `${s.poePorts} PoE ports, ${s.poeStandard}` : `${s.poePorts} PoE ports`;
  }
  if (e.category === "ups") return s.runtimeAtHalfLoadMin ? `${s.runtimeAtHalfLoadMin} min at half load` : "";
  if (e.category === "power-supply") return s.batteryBacked ? "battery backup" : "";
  return "";
}

const EquipmentPage = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const item = category && slug ? getEquipmentByPath(category, slug) : undefined;
  const info = item ? categoryInfo(item.category) : undefined;
  const one = info?.one ?? "Equipment";
  const brandInModel = !!item && item.model.toLowerCase().includes(item.manufacturer.toLowerCase());
  const fullName = item ? (brandInModel ? item.model : `${item.manufacturer} ${item.model}`) : "";
  const hook = item ? titleHook(item, one) : "";
  const sub = item ? subtitle(item) : "";
  const cameras = item ? getCamerasForMount(item) : [];
  const url = item ? `${SITE}${item.path}` : SITE;

  const seoTitle = item ? `${item.model} Specs — ${hook} | CCTVplanner` : "Not found | CCTVplanner";
  const seoDescription = item
    ? `${fullName}: ${hook}${sub ? `, ${sub}` : ""}${cameras.length ? `; fits ${cameras.length} ${item.manufacturer} camera models` : ""}. Specifications from ${item.manufacturer}'s own datasheet. Plan it into a CCTV or security layout for free.`
    : "";

  useEffect(() => {
    if (!item) return;
    updateMetaTags({
      title: seoTitle,
      description: seoDescription,
      keywords: `${fullName}, ${item.model} specs, ${item.model} datasheet, ${item.manufacturer} ${one.toLowerCase()}`,
      ogTitle: seoTitle,
      ogDescription: seoDescription,
      canonicalUrl: url,
      ogType: "product",
    });
    window.scrollTo(0, 0);
  }, [item, seoTitle, seoDescription, fullName, one, url]);

  if (!item || !info) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Head>
          <title>{t("equipment_not_found_title", { defaultValue: "Not found — CCTVplanner" })}</title>
          <meta name="robots" content="noindex,follow" />
          <meta name="prerender-status-code" content="404" />
        </Head>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">{t("equipment_not_found", { defaultValue: "Equipment not found" })}</h1>
          <Link to="/equipment" className="text-[var(--color-brand-blue-text)] hover:underline">
            {t("equipment_back_to_catalog", { defaultValue: "Back to the equipment catalog" })}
          </Link>
        </div>
      </div>
    );
  }

  const rows = getSpecRows(item);
  const note = getPlanningNote(item);
  const related = getRelatedEquipment(item);
  const alternatives = getAlternativeEquipment(item);
  const handleCTA = () => navigate(user ? "/cctv-design" : "/demo");
  const mono = { fontFamily: "var(--font-mono), monospace", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase" as const, color: "var(--color-gray-muted)" };
  const card = "bg-[var(--color-surface)] border border-[var(--color-gray-border)] rounded-none p-4 hover:border-[var(--color-brand-blue)] transition-colors group";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <link rel="canonical" href={url} />
        <link rel="alternate" hrefLang="en" href={url} />
        <link rel="alternate" hrefLang="x-default" href={url} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={`${SITE}/og/home.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Head>

      <SchemaScript schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Equipment", url: "/equipment" },
        { name: info.many, url: `/equipment/${item.category}` },
        { name: item.model, url: item.path },
      ])} />
      <SchemaScript schema={{
        "@context": "https://schema.org",
        "@type": "TechArticle",
        headline: `${fullName} — ${hook}`,
        description: seoDescription,
        image: `${SITE}/og/home.png`,
        url,
        mainEntityOfPage: url,
        inLanguage: "en",
        isAccessibleForFree: true,
        author: { "@type": "Organization", name: "CCTVplanner", url: SITE },
        publisher: { "@type": "Organization", name: "CCTVplanner", url: SITE, logo: { "@type": "ImageObject", url: `${SITE}/og/home.png` } },
        about: {
          "@type": "Product",
          name: fullName,
          brand: { "@type": "Brand", name: item.manufacturer },
          manufacturer: { "@type": "Organization", name: item.manufacturer },
          category: one,
          sku: item.model,
          mpn: item.model,
          additionalProperty: rows.slice(0, 12).map(r => ({ "@type": "PropertyValue", name: r.label, value: r.value })),
        },
      }} />

      <MarketingHeader />

      <main id="main-content">
        <section className="border-b" style={{ paddingTop: "calc(var(--header-height) + 4rem)", paddingBottom: "4rem", borderColor: "var(--color-gray-border)" }}>
          <div className="pelco-container">
            <nav className="flex flex-wrap items-center gap-2 text-[12px] mb-6" style={{ fontFamily: "var(--font-mono), monospace", color: "var(--color-gray-muted)" }} aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground transition-colors">{t("breadcrumb_home", { defaultValue: "Home" })}</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/equipment" className="hover:text-foreground transition-colors">{t("equipment_breadcrumb", { defaultValue: "Equipment" })}</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/equipment/${item.category}`} className="hover:text-foreground transition-colors">{info.many}</Link>
              <ChevronRight className="w-3 h-3" />
              <span style={{ color: "var(--color-gray-dark)" }}>{item.model}</span>
            </nav>
            <p className="bd-eyebrow">CCTVPLANNER.IO · {one.toUpperCase()} · {item.manufacturer.toUpperCase()}</p>
            <h1 className="bd-h1 max-w-3xl">{fullName}</h1>
            <p className="bd-lead max-w-3xl">
              {item.tagline
                || (cameras.length
                  ? `${hook} that ${item.manufacturer} lists as an accessory of ${cameras.length} of its camera models.`
                  : `${hook}${sub ? `, ${sub}` : ""}.`)}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={handleCTA} className="bd-btn bd-btn-primary">
                {t("equipment_plan_cta", { defaultValue: "Plan a layout with it" })}
                <ArrowRight className="w-4 h-4" />
              </button>
              <a href={item.datasheetUrl} target="_blank" rel="noopener noreferrer nofollow" className="bd-btn bd-btn-secondary">
                {t("equipment_view_source", { defaultValue: "View {{manufacturer}}'s page", manufacturer: item.manufacturer })}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        <div className="pelco-container py-12">
          <section className="mb-16">
            <p className="bd-eyebrow">SPECIFICATIONS · {item.model.toUpperCase()}</p>
            <h2 className="bd-h2 mb-6">{t("equipment_specifications", { defaultValue: "Specifications" })}</h2>
            <div className="border" style={{ borderColor: "var(--color-gray-border)", backgroundColor: "var(--color-surface)" }}>
              {rows.map((r, i) => (
                <div key={r.label} className="flex justify-between items-baseline gap-6 px-6 py-4" style={{ borderTop: i !== 0 ? "1px solid var(--color-gray-border)" : undefined }}>
                  <span style={mono}>{r.label}</span>
                  <span className="text-foreground font-medium text-right">{r.value}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-[var(--color-gray-muted)] mt-3">
              {t("equipment_source_note", { defaultValue: "Figures as {{manufacturer}} states them on the page linked above; nothing here is estimated.", manufacturer: item.manufacturer })}
            </p>
          </section>

          {note && (
            <section className="mb-16 max-w-3xl">
              <h2 className="bd-h2 mb-4">{t("equipment_planning", { defaultValue: "In a camera plan" })}</h2>
              <p className="text-[var(--color-gray-muted)] leading-relaxed">{note}</p>
            </section>
          )}

          {cameras.length > 0 && (
            <section className="mb-16">
              <h2 className="bd-h2 mb-4">
                {t("equipment_mount_cameras", { defaultValue: "Cameras the {{model}} fits", model: item.model })}
              </h2>
              <p className="text-[var(--color-gray-muted)] max-w-3xl mb-6">
                {t("equipment_mount_cameras_desc", {
                  defaultValue: "{{count}} camera models whose {{manufacturer}} product page lists the {{model}} among its accessories.",
                  count: cameras.length,
                  manufacturer: item.manufacturer,
                  model: item.model,
                })}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                {cameras.slice(0, MAX_CAMERAS).map(c => (
                  <li key={`${c.manufacturer}|${c.model}`} className="py-1 text-sm">
                    {c.path
                      ? <Link to={c.path} className="hover:text-[var(--color-brand-blue-text)] transition-colors">{c.model}</Link>
                      : <span className="text-[var(--color-gray-muted)]">{c.model}</span>}
                  </li>
                ))}
              </ul>
              {cameras.length > MAX_CAMERAS && (
                <p className="text-sm text-[var(--color-gray-muted)] mt-3">
                  {t("equipment_mount_cameras_more", { defaultValue: "And {{count}} more.", count: cameras.length - MAX_CAMERAS })}
                </p>
              )}
            </section>
          )}

          {related.length > 0 && (
            <section className="mb-16">
              <h2 className="bd-h2 mb-8">{t("equipment_related", { defaultValue: "More from {{manufacturer}}", manufacturer: item.manufacturer })}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {related.map(o => (
                  <Link key={o.path} to={o.path} className={card}>
                    <p className="text-xs text-[var(--color-brand-blue-text)] mb-1">{o.manufacturer}</p>
                    <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{o.model}</p>
                    <p className="text-xs text-[var(--color-gray-muted)] mt-1">{titleHook(o, one)}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {alternatives.length > 0 && (
            <section className="mb-16">
              <h2 className="bd-h2 mb-8">{t("equipment_alternatives", { defaultValue: "Comparable models from other makers" })}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {alternatives.map(o => (
                  <Link key={o.path} to={o.path} className={card}>
                    <p className="text-xs text-[var(--color-brand-blue-text)] mb-1">{o.manufacturer}</p>
                    <p className="font-semibold text-foreground group-hover:text-[var(--color-brand-blue-text)] transition-colors text-sm">{o.model}</p>
                    <p className="text-xs text-[var(--color-gray-muted)] mt-1">{titleHook(o, one)}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="mb-16">
            <h2 className="bd-h2 mb-8">{t("equipment_tools", { defaultValue: "Tools" })}</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <Link to="/cctv-design-tool" className={card}>
                <PencilRuler className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground text-sm">{t("nvr_tool_design_title", { defaultValue: "Free CCTV Design Tool" })}</p>
              </Link>
              <Link to="/calculator/ups" className={card}>
                <Zap className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground text-sm">{t("nvr_tool_ups_title", { defaultValue: "UPS Sizing Calculator" })}</p>
              </Link>
              <Link to="/cameras" className={card}>
                <Camera className="w-5 h-5 text-[var(--color-brand-blue-text)] mb-2" />
                <p className="font-semibold text-foreground text-sm">{t("equipment_camera_catalog", { defaultValue: "Camera catalog" })}</p>
              </Link>
            </div>
          </section>
        </div>

        <section className="bd-section bd-section--dark always-dark py-20 md:py-28" style={{ backgroundColor: "var(--color-gray-dark)" }}>
          <div className="pelco-container text-center max-w-3xl">
            <h2 className="font-bold mb-6" style={{ fontFamily: "var(--font-body), 'Instrument Sans', sans-serif", fontSize: "clamp(28px, 4vw, 40px)", letterSpacing: "-0.02em", lineHeight: 1.1, color: "var(--color-page-bg)" }}>
              {t("equipment_plan_cta_title", { defaultValue: "Plan a system with the {{name}}", name: fullName })}
            </h2>
            <button onClick={handleCTA} className="bd-btn bd-btn-primary" style={{ backgroundColor: "var(--color-page-bg)", color: "var(--color-gray-dark)", borderColor: "var(--color-page-bg)" }}>
              {t("nvr_start_planning_free", { defaultValue: "Start planning for free" })}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default EquipmentPage;
